import React, { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

import zoniqLogo from "../../logos/zoniq.png";
import landingMarkup from "./landingMarkup";
import "./LandingPage.css";

const ROLES: Record<string, { t: string; q: string; l: string[]; m: [string, string][] }> = {
  organisers: {
    t: "Event Organisers",
    q: "We sell out, but we still find out what we made three weeks after the show.",
    l: [
      "Sell on your own event page, with tiers and phases you control",
      "See revenue against costs while tickets are still on sale",
      "Keep every buyer’s record for your next announcement",
    ],
    m: [["Ticketing", "live"], ["Finance", "ok"], ["Audience", "gold"]],
  },
  promoters: {
    t: "Festival Promoters",
    q: "Know your sell-through and revenue in real time on show day - not three weeks later when finance closes the books.",
    l: [
      "Multi-day, multi-tier inventory in one view",
      "Gate and lane check-ins on show day",
      "Channel-by-channel net revenue and settlements",
    ],
    m: [["Ticketing", "live"], ["Operations", "cy"], ["Finance", "ok"]],
  },
  agencies: {
    t: "Event Agencies",
    q: "Running five events at once shouldn’t mean five different spreadsheets.",
    l: [
      "Every event’s budget, vendor payments and team assignments in one place",
      "Portfolio view across clients and cities",
      "GST-ready invoices and a clean P&L for every client",
    ],
    m: [["EventOS", "teal"], ["Finance", "ok"], ["Analytics", ""]],
  },
  venues: {
    t: "Venue Operators",
    q: "Turn occupancy and event performance into decisions you can act on before the next show, not after.",
    l: [
      "Compare nights, formats and promoters side by side",
      "Recognise regulars across every event you host",
      "Door and guest-list data that stays with the venue",
    ],
    m: [["Audience", "gold"], ["Analytics", ""], ["Ticketing", "live"]],
  },
  talent: {
    t: "Artist & Talent Teams",
    q: "Coordinate tours, hospitality riders and logistics without fifteen WhatsApp threads and a lost spreadsheet.",
    l: [
      "Riders, travel and schedules attached to each show",
      "One shared run-of-show with the promoter’s team",
      "Clear view of fees, advances and what’s been paid",
    ],
    m: [["EventOS", "teal"], ["Operations", "cy"], ["Finance", "ok"]],
  },
  corporate: {
    t: "Corporate Event Teams",
    q: "Plan and report on internal events without rebuilding a tracker from scratch every time.",
    l: [
      "Reusable event templates for tasks and budgets",
      "RSVPs, guest lists and check-ins in one place",
      "Spend reports ready for finance and leadership",
    ],
    m: [["EventOS", "teal"], ["Audience", "gold"], ["Finance", "ok"]],
  },
};

const SCREEN_TITLES = [
  "Ticketing · Sales today",
  "EventOS · Your events",
  "Finance · Gross to margin",
  "Audience · Segments",
  "Operations · Show day",
  "Analytics · Pace across events",
];

const AREAS = ["Bandra", "Andheri", "Powai", "Thane", "Pune", "Juhu", "Colaba", "Navi Mumbai", "Worli", "Goregaon"];
const TIERS: [string, number, number][] = [["GA", 1, 4], ["VIP", 1, 2], ["Fan Pit", 1, 2]];

const LandingPage: React.FC = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const $ = (s: string, r: ParentNode = root) => r.querySelector<HTMLElement>(s);
    const $$ = (s: string, r: ParentNode = root) => Array.from(r.querySelectorAll<HTMLElement>(s));
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const cleanups: Array<() => void> = [];
    const on = (t: EventTarget, ev: string, fn: EventListener) => {
      t.addEventListener(ev, fn);
      cleanups.push(() => t.removeEventListener(ev, fn));
    };
    const every = (fn: () => void, ms: number) => {
      const id = window.setInterval(fn, ms);
      cleanups.push(() => window.clearInterval(id));
    };

    /* match the standalone page: default scrollbar (same page width), smooth scroll, page bg */
    const html = document.documentElement;
    const prev = {
      behavior: html.style.scrollBehavior,
      sbWidth: html.style.getPropertyValue("scrollbar-width"),
      sbColor: html.style.getPropertyValue("scrollbar-color"),
      bg: document.body.style.background,
      htmlBg: html.style.background,
    };
    html.style.scrollBehavior = "smooth";
    html.style.setProperty("scrollbar-width", "auto");
    html.style.setProperty("scrollbar-color", "auto");
    html.style.background = "#0b0b0f";
    document.body.style.background = "#0b0b0f";
    html.classList.add("zl-page");
    cleanups.push(() => {
      html.style.scrollBehavior = prev.behavior;
      html.style.setProperty("scrollbar-width", prev.sbWidth);
      html.style.setProperty("scrollbar-color", prev.sbColor);
      html.style.background = prev.htmlBg;
      document.body.style.background = prev.bg;
      html.classList.remove("zl-page");
      document.body.style.overflow = "";
    });

    /* in-app links (Sign in etc.) use the router */
    on(root, "click", ((e: MouseEvent) => {
      const a = (e.target as Element).closest("a");
      const href = a?.getAttribute("href");
      if (a && href && href.startsWith("/") && !a.target && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        navigate(href);
      }
    }) as EventListener);

    /* nav dropdowns */
    const closeDd = () =>
      $$(".dd").forEach((x) => {
        x.classList.remove("open");
        $("button", x)?.setAttribute("aria-expanded", "false");
      });
    $$(".dd").forEach((dd) => {
      const b = $("button", dd);
      if (!b) return;
      on(b, "click", ((e: Event) => {
        e.stopPropagation();
        const o = !dd.classList.contains("open");
        closeDd();
        dd.classList.toggle("open", o);
        b.setAttribute("aria-expanded", String(o));
      }) as EventListener);
      on(dd, "mouseenter", () => {
        if (window.matchMedia("(hover:hover)").matches) {
          dd.classList.add("open");
          b.setAttribute("aria-expanded", "true");
        }
      });
      on(dd, "mouseleave", () => {
        dd.classList.remove("open");
        b.setAttribute("aria-expanded", "false");
      });
    });
    on(document, "click", closeDd);

    /* mobile nav */
    const m = $("#mnav");
    const burger = $(".nav-cta .burger");
    const closeM = () => {
      if (!m || !burger) return;
      m.classList.remove("open");
      m.setAttribute("aria-hidden", "true");
      burger.setAttribute("aria-expanded", "false");
      document.body.style.overflow = "";
    };
    on(document, "keydown", ((e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeDd();
        closeM();
      }
    }) as EventListener);
    if (m && burger) {
      on(burger, "click", () => {
        m.classList.add("open");
        m.setAttribute("aria-hidden", "false");
        burger.setAttribute("aria-expanded", "true");
        document.body.style.overflow = "hidden";
      });
      const closeBtn = $("[data-close]", m);
      if (closeBtn) on(closeBtn, "click", closeM);
      $$("a", m).forEach((a) => on(a, "click", closeM));
    }

    /* counters */
    const fmt = (v: number, d: number) => (d ? v.toFixed(d) : Math.round(v).toLocaleString("en-IN"));
    const count = (el: HTMLElement) => {
      if (el.dataset.done) return;
      el.dataset.done = "1";
      const end = parseFloat(el.dataset.count || "0");
      const d = +(el.dataset.dec || 0);
      const t0 = performance.now();
      const step = (t: number) => {
        const k = Math.min(1, (t - t0) / 1400);
        el.textContent = fmt(end * (1 - Math.pow(1 - k, 3)), d);
        if (k < 1) requestAnimationFrame(step);
      };
      step(t0);
    };
    const hasIO = "IntersectionObserver" in window;
    if (!reduce && hasIO) {
      const io = new IntersectionObserver(
        (es) =>
          es.forEach((en) => {
            if (en.isIntersecting) {
              count(en.target as HTMLElement);
              io.unobserve(en.target);
            }
          }),
        { threshold: 0.4 },
      );
      $$("[data-count]").forEach((el) => io.observe(el));
      cleanups.push(() => io.disconnect());

      const stages = $$("#cycle .stage-n");
      const cycle = $("#cycle");
      let li = 0;
      let lt: number | null = null;
      const cio = new IntersectionObserver(
        (es) =>
          es.forEach((en) => {
            if (en.isIntersecting && lt === null) {
              lt = window.setInterval(() => {
                stages.forEach((s) => s.classList.remove("lit"));
                stages[li % stages.length]?.classList.add("lit");
                li++;
              }, 1100);
            } else if (!en.isIntersecting && lt !== null) {
              clearInterval(lt);
              lt = null;
              stages.forEach((s) => s.classList.remove("lit"));
            }
          }),
        { threshold: 0.3 },
      );
      if (cycle) cio.observe(cycle);
      cleanups.push(() => {
        cio.disconnect();
        if (lt !== null) clearInterval(lt);
      });
    }

    /* live order feed (sample) */
    const feed = $("#feed");
    if (feed && !reduce) {
      every(() => {
        if (document.hidden) return;
        const t = TIERS[Math.random() < 0.6 ? 0 : Math.random() < 0.7 ? 1 : 2];
        const q = t[1] + Math.floor(Math.random() * t[2]);
        $$(".o span:last-child", feed).forEach((s, i) => {
          s.textContent = ["1m", "3m", "5m", "8m", "11m", "14m"][i] || "15m";
        });
        const d = document.createElement("div");
        d.className = "o";
        d.innerHTML = `<span>${q} × ${t[0]} · ${AREAS[Math.floor(Math.random() * AREAS.length)]}</span><span>just now</span>`;
        feed.prepend(d);
        while (feed.children.length > 6) feed.lastElementChild?.remove();
      }, 3200);
    }

    /* EventOS workflow steps */
    const steps = $$(".steps button");
    const thumb = $(".steps .thumb");
    const panels = $$(".panel");
    const sideNavs = $$(".side a");
    const app = $(".app");
    let eosTimer: number | null = null;
    let eosI = 0;
    const stopEos = () => {
      if (eosTimer !== null) clearInterval(eosTimer);
    };
    const setStep = (i: number) => {
      eosI = i;
      steps.forEach((b, j) => b.setAttribute("aria-selected", String(j === i)));
      if (thumb) thumb.style.transform = `translateX(${i * 100}%)`;
      panels.forEach((p, j) => p.classList.toggle("on", j === i));
      sideNavs.forEach((n, j) => n.classList.toggle("on", j === i));
    };
    steps.forEach((b, i) => {
      on(b, "click", () => {
        setStep(i);
        stopEos();
      });
      on(b, "keydown", ((e: KeyboardEvent) => {
        if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
          const n = (i + (e.key === "ArrowRight" ? 1 : steps.length - 1)) % steps.length;
          setStep(n);
          steps[n].focus();
          stopEos();
        }
      }) as EventListener);
    });
    if (!reduce && hasIO && app) {
      const eio = new IntersectionObserver(
        (es) =>
          es.forEach((en) => {
            if (en.isIntersecting && eosTimer === null) {
              eosTimer = window.setInterval(() => setStep((eosI + 1) % steps.length), 5200);
            }
          }),
        { threshold: 0.5 },
      );
      eio.observe(app);
      cleanups.push(() => eio.disconnect());
    }
    cleanups.push(stopEos);

    /* Who it's for */
    const rc = $("#rc");
    const rbtn = $$(".roles button");
    const setRole = (k: string) => {
      const r = ROLES[k];
      if (!r || !rc) return;
      rbtn.forEach((b) => b.setAttribute("aria-selected", String(b.dataset.role === k)));
      rc.innerHTML =
        `<span class="eyebrow">${r.t}</span><p class="q">${r.q}</p>` +
        `<ul>${r.l.map((x) => `<li>${x}</li>`).join("")}</ul>` +
        `<div class="mods"><span class="muted" style="font-size:13px;margin-right:4px">Uses</span>` +
        `${r.m.map(([n, c]) => `<span class="tag ${c}">${n}</span>`).join("")}</div>`;
      rc.style.animation = "none";
      void rc.offsetHeight;
      rc.style.animation = "";
    };
    rbtn.forEach((b) => on(b, "click", () => setRole(b.dataset.role || "")));
    $$("a[data-role]").forEach((a) => on(a, "click", () => setRole(a.dataset.role || "")));
    setRole("organisers");

    /* Product showcase */
    const sb = $$(".stabs button");
    const scr = $$(".scr");
    const title = $("#scr-title");
    const show = $("#showcase");
    let si = 0;
    let st: number | null = null;
    let paused = false;
    const setS = (i: number) => {
      si = i;
      sb.forEach((b, j) => {
        b.setAttribute("aria-selected", String(j === i));
        const p = $(".prog", b);
        if (p) {
          p.style.animation = "none";
          void p.offsetHeight;
          p.style.animation = "";
        }
      });
      scr.forEach((s, j) => s.classList.toggle("on", j === i));
      if (title) title.textContent = SCREEN_TITLES[i];
      $$("[data-count]", scr[i]).forEach((el) => {
        delete el.dataset.done;
        if (!reduce) count(el);
      });
    };
    const arm = () => {
      if (st !== null) clearInterval(st);
      st = null;
      if (!reduce) {
        st = window.setInterval(() => {
          if (!paused && !document.hidden) setS((si + 1) % sb.length);
        }, 7000);
      }
    };
    sb.forEach((b, i) =>
      on(b, "click", () => {
        setS(i);
        arm();
      }),
    );
    if (show) {
      on(show, "mouseenter", () => {
        paused = true;
        show.classList.add("paused");
      });
      on(show, "mouseleave", () => {
        paused = false;
        show.classList.remove("paused");
      });
      if (hasIO) {
        const sio = new IntersectionObserver(
          (es) =>
            es.forEach((en) => {
              if (en.isIntersecting) {
                if (st === null) arm();
              } else {
                if (st !== null) clearInterval(st);
                st = null;
              }
            }),
          { threshold: 0.3 },
        );
        sio.observe(show);
        cleanups.push(() => sio.disconnect());
      }
    }
    cleanups.push(() => {
      if (st !== null) clearInterval(st);
    });

    /* copy email */
    const copy = $("#copy");
    if (copy) {
      on(copy, "click", (async () => {
        const em = $("#em");
        try {
          await navigator.clipboard.writeText(em?.textContent || "");
          copy.textContent = "Copied";
        } catch {
          if (em) {
            const r = document.createRange();
            r.selectNodeContents(em);
            const s = window.getSelection();
            s?.removeAllRanges();
            s?.addRange(r);
          }
          copy.textContent = "Selected";
        }
        window.setTimeout(() => {
          copy.textContent = "Copy";
        }, 1800);
      }) as unknown as EventListener);
    }

    /* the markup is injected after mount, so the browser cannot jump to /#anchor on its own */
    if (window.location.hash.length > 1) {
      const id = decodeURIComponent(window.location.hash.slice(1));
      const raf = window.requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView());
      cleanups.push(() => window.cancelAnimationFrame(raf));
    }

    return () => cleanups.forEach((fn) => fn());
  }, [navigate]);

  const html = useMemo(
    () => landingMarkup.split("__ZONIQ_LOGO_SRC__").join(zoniqLogo),
    [],
  );

  return <div className="zl" ref={rootRef} dangerouslySetInnerHTML={{ __html: html }} />;
};

export default LandingPage;
